import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export interface ActiveTimer {
    issueId: number
    projectId: number
    issueSubject: string
    startTime: number // timestamp
    activityId: number
    activityName: string
    description: string
    pausedTime: number // Total paused time in seconds
    isPaused: boolean
}

export interface TimeEntry {
    id: number
    issueId: number
    hours: number
    comments: string
    spent_on: string
    activity: { id: number; name: string }
    user: { id: number; name: string }
    created_on: string
}

interface TimeTrackingState {
    activeTimer: ActiveTimer | null
    recentEntries: TimeEntry[]

    // Actions
    startTimer: (timer: Omit<ActiveTimer, 'startTime' | 'pausedTime' | 'isPaused'>) => void
    stopTimer: () => ActiveTimer | null
    pauseTimer: () => void
    resumeTimer: () => void
    getElapsedTime: () => number
    getTotalHours: () => number
    getFormattedTime: () => string
    updateDescription: (description: string) => void
    addTimeEntry: (entry: TimeEntry) => void
    clearRecentEntries: () => void
}

export const useTimeTracking = create<TimeTrackingState>()(
    persist(
        (set, get) => ({
            activeTimer: null,
            recentEntries: [],

            startTimer: (timer) => {
                const now = Date.now()
                const newTimer = {
                    ...timer,
                    startTime: now,
                    pausedTime: 0,
                    isPaused: false
                }

                set({ activeTimer: newTimer })
            },

            stopTimer: () => {
                const { activeTimer } = get()
                set({ activeTimer: null })
                return activeTimer
            },

            pauseTimer: () => {
                const { activeTimer } = get()
                if (!activeTimer || activeTimer.isPaused) return

                const elapsed = Math.floor((Date.now() - activeTimer.startTime) / 1000)
                set({
                    activeTimer: {
                        ...activeTimer,
                        pausedTime: activeTimer.pausedTime + elapsed,
                        isPaused: true,
                        startTime: Date.now() // Reset start time for next resume
                    }
                })
            },

            resumeTimer: () => {
                const { activeTimer } = get()
                if (!activeTimer || !activeTimer.isPaused) return

                set({
                    activeTimer: {
                        ...activeTimer,
                        isPaused: false,
                        startTime: Date.now()
                    }
                })
            },

            getElapsedTime: () => {
                const { activeTimer } = get()
                if (!activeTimer) return 0

                if (activeTimer.isPaused) {
                    return activeTimer.pausedTime
                }

                const currentElapsed = Math.floor((Date.now() - activeTimer.startTime) / 1000)
                return activeTimer.pausedTime + currentElapsed
            },

            getTotalHours: () => {
                const seconds = get().getElapsedTime()
                return parseFloat((seconds / 3600).toFixed(2))
            },

            getFormattedTime: () => {
                const totalSeconds = get().getElapsedTime()
                const hours = Math.floor(totalSeconds / 3600)
                const minutes = Math.floor((totalSeconds % 3600) / 60)
                const seconds = totalSeconds % 60

                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            },

            updateDescription: (description: string) => {
                const { activeTimer } = get()
                if (!activeTimer) return

                set({
                    activeTimer: {
                        ...activeTimer,
                        description
                    }
                })
            },

            addTimeEntry: (entry) => {
                set((state) => ({
                    recentEntries: [entry, ...state.recentEntries].slice(0, 10) // Keep last 10
                }))
            },

            clearRecentEntries: () => {
                set({ recentEntries: [] })
            }
        }),
        {
            name: 'orbitflow-time-tracking',
            storage: createJSONStorage(() => localStorage),
        }
    )
)